export const APP_SCHEMA_VERSION = 1;
export const COACH_SCHEMA_VERSION = "1.0";

export const isoNow = () => new Date().toISOString();
export const makeId = (prefix = "id") => `${prefix}_${crypto.randomUUID?.() || Math.random().toString(36).slice(2)}`;
export const clone = value => JSON.parse(JSON.stringify(value));
export const clamp = (value, min, max) => Math.min(max, Math.max(min, Number(value)));

const today = () => new Date().toISOString().slice(0, 10);

export function createDefaultState() {
  const createdAt = isoNow();
  return {
    schemaVersion: APP_SCHEMA_VERSION,
    revision: 1,
    createdAt,
    modifiedAt: createdAt,
    profile: {
      id: "profile_primary",
      name: "Athlete",
      birthDate: "",
      units: "imperial",
      trainingFrequency: 4,
      notes: "Build durable strength, conditioning, and recovery habits."
    },
    metricDefinitions: [
      { id: "metric_body_weight", name: "Body weight", unit: "lb", category: "body", archived: false },
      { id: "metric_waist", name: "Waist", unit: "in", category: "body", archived: false },
      { id: "metric_steps", name: "Steps", unit: "steps", category: "activity", archived: false },
      { id: "metric_vertical", name: "Vertical jump", unit: "in", category: "performance", archived: false }
    ],
    measurements: [],
    programs: [starterProgram(createdAt)],
    activeProgramId: "program_foundation",
    activeProgramVersion: 1,
    nutritionTargets: [],
    dailyNutritionOverrides: [],
    savedFoods: [],
    savedMeals: [],
    nutritionSchedule: [],
    nutritionPlan: { weightGoal: null, unit: "lb", targetDate: "", notes: "" },
    nutritionLogs: [],
    goals: [],
    milestones: [],
    completedWorkouts: [],
    activityLogs: [],
    recoveryNotes: [],
    coachingNotes: [],
    coachUpdateHistory: [],
    migrationHistory: [{ from: 0, to: 1, at: createdAt }]
  };
}

function starterProgram(createdAt) {
  const exercise = (id, name, sets, reps, load, rpe, rest, notes = "") => ({
    id, exerciseDefinitionId: `exercise_${id}`, name, sets, reps, targetLoad: load,
    loadUnit: "lb", durationMinutes: null, restSeconds: rest, rpe, rir: null,
    instructions: notes, coachingNotes: "Leave one clean rep in reserve if technique changes.",
    alternatives: [], progressionRule: "When every work set meets the top of the rep range at target RPE, add 5 lb next exposure."
  });
  return {
    id: "program_foundation", version: 1, name: "Foundation Block", effectiveDate: "2026-10-26",
    createdAt, modifiedAt: createdAt, archived: false, coachingNotes: "Editable starter program. Build consistency before intensity.",
    activePhaseId: "phase_foundation_1",
    phases: [{ id: "phase_foundation_1", name: "Foundation", startDate: "2026-10-26", endDate: "2026-12-06", active: true, notes: "Six-week base phase." }],
    schedule: [
      { id: "schedule_mon", weekday: 1, templateId: "template_lower", time: "17:30" },
      { id: "schedule_tue", weekday: 2, templateId: "template_upper", time: "17:30" },
      { id: "schedule_thu", weekday: 4, templateId: "template_lower", time: "17:30" },
      { id: "schedule_sat", weekday: 6, templateId: "template_conditioning", time: "10:00" }
    ],
    workoutTemplates: [
      { id: "template_lower", name: "Lower Strength", phaseId: "phase_foundation_1", notes: "Controlled eccentric; crisp reps.", archived: false, exercises: [
        exercise("squat", "Back squat", 4, "5", 185, 7, 150, "Brace before each rep and keep mid-foot pressure."),
        exercise("rdl", "Romanian deadlift", 3, "8", 155, 7, 120, "Hinge until hamstrings limit the range."),
        exercise("split_squat", "Rear-foot elevated split squat", 3, "8/side", 30, 8, 90, "Stay tall and control the bottom position.")
      ]},
      { id: "template_upper", name: "Upper Strength", phaseId: "phase_foundation_1", notes: "Full range without shoulder irritation.", archived: false, exercises: [
        exercise("bench", "Bench press", 4, "6", 145, 7, 120, "Use a stable arch and pause softly on the chest."),
        exercise("row", "Chest-supported row", 4, "8", 70, 8, 90, "Finish by pulling elbows behind the torso."),
        exercise("press", "Half-kneeling landmine press", 3, "10/side", 45, 7, 75, "Reach at the top without shrugging.")
      ]},
      { id: "template_conditioning", name: "Aerobic Base", phaseId: "phase_foundation_1", notes: "Conversational pace.", archived: false, exercises: [
        { ...exercise("zone2", "Zone 2 cardio", 1, "continuous", null, 5, 0, "Keep breathing controlled and sustainable."), durationMinutes: 35, loadUnit: "none", progressionRule: "Add 5 minutes when recovery remains good, up to 50 minutes." }
      ]}
    ],
    modificationHistory: [{ at: createdAt, source: "seed", reason: "Original editable starter prescription" }]
  };
}

export function activeProgram(state) {
  return state.programs.find(p => p.id === state.activeProgramId && p.version === state.activeProgramVersion) || null;
}

export function nutritionTargetForDate(state, date) {
  return state.nutritionTargets
    .filter(t => t.effectiveDate <= date)
    .sort((a, b) => b.effectiveDate.localeCompare(a.effectiveDate))[0] || null;
}

export function completeWorkout(state, templateId, actualExercises = [], date = today()) {
  const program = activeProgram(state);
  const template = program?.workoutTemplates.find(t => t.id === templateId);
  if (!program || !template) throw new Error("Workout template not found.");
  const next = clone(state);
  next.completedWorkouts.push({
    id: makeId("completed"), date, programId: program.id, programVersion: program.version,
    templateId: template.id, templateName: template.name, prescriptionSnapshot: clone(template),
    actualExercises: clone(actualExercises), completedAt: isoNow()
  });
  return touch(next);
}

export function versionProgram(state, mutate, reason = "Manual edit", effectiveDate = today()) {
  const current = activeProgram(state);
  if (!current) throw new Error("No active program.");
  const next = clone(state);
  const versions = next.programs.filter(p => p.id === current.id).map(p => p.version);
  const revised = clone(current);
  revised.version = Math.max(...versions) + 1;
  revised.effectiveDate = effectiveDate;
  revised.createdAt = isoNow();
  revised.modifiedAt = revised.createdAt;
  revised.modificationHistory = [...(revised.modificationHistory || []), { at: revised.createdAt, source: "manual", reason }];
  mutate(revised);
  next.programs.push(revised);
  next.activeProgramVersion = revised.version;
  return touch(next);
}

function touch(state) {
  state.revision = Number(state.revision || 0) + 1;
  state.modifiedAt = isoNow();
  return state;
}

const allowedOperations = new Set([
  "schedule.set", "workout_template.patch", "exercise_prescription.patch", "exercise.add",
  "exercise.replace", "nutrition_target.add", "goal.patch", "training_phase.add",
  "coaching_note.add", "metric_target.patch"
]);

export function validateCoachUpdate(pkg, state) {
  const errors = [];
  const warnings = [];
  if (!pkg || typeof pkg !== "object" || Array.isArray(pkg)) return { valid: false, errors: ["Update must be a JSON object."], warnings };
  rejectUnknown(pkg, ["schemaVersion", "updateId", "createdAt", "baseRevision", "source", "summary", "operations"], "update", errors);
  if (pkg.schemaVersion !== COACH_SCHEMA_VERSION) errors.push(`schemaVersion must be ${COACH_SCHEMA_VERSION}.`);
  if (!isId(pkg.updateId)) errors.push("updateId must be a stable identifier using letters, numbers, _ or -.");
  if (!validDateTime(pkg.createdAt)) errors.push("createdAt must be an ISO date-time.");
  if (!Number.isInteger(pkg.baseRevision) || pkg.baseRevision < 1) errors.push("baseRevision must be a positive integer.");
  if (state && pkg.baseRevision !== state.revision) warnings.push(`Stale package: built for revision ${pkg.baseRevision}; current revision is ${state.revision}.`);
  if (!pkg.source || typeof pkg.source.name !== "string") errors.push("source.name is required.");
  else rejectUnknown(pkg.source, ["name", "model", "conversationReference"], "source", errors);
  if (typeof pkg.summary !== "string" || !pkg.summary.trim()) errors.push("summary is required.");
  if (!Array.isArray(pkg.operations) || !pkg.operations.length) errors.push("operations must contain at least one operation.");
  const seen = new Set();
  (pkg.operations || []).forEach((op, i) => {
    const path = `operations[${i}]`;
    if (op && typeof op === "object") rejectUnknown(op, ["id", "type", "reason", "payload"], path, errors);
    if (!isId(op?.id)) errors.push(`${path}.id is invalid.`);
    if (seen.has(op?.id)) errors.push(`${path}.id is duplicated.`);
    seen.add(op?.id);
    if (!allowedOperations.has(op?.type)) errors.push(`${path}.type is unsupported.`);
    if (typeof op?.reason !== "string" || !op.reason.trim()) errors.push(`${path}.reason is required.`);
    if (!op?.payload || typeof op.payload !== "object" || Array.isArray(op.payload)) errors.push(`${path}.payload must be an object.`);
    validateOperation(op, state, path, errors);
  });
  return { valid: errors.length === 0, errors, warnings };
}

function validateOperation(op, state, path, errors) {
  const p = op?.payload || {};
  const program = state ? activeProgram(state) : null;
  const template = id => program?.workoutTemplates.find(t => t.id === id);
  const exercise = (t, id) => template(t)?.exercises.find(e => e.id === id);
  const date = (v, field) => { if (!validDate(v)) errors.push(`${path}.payload.${field} must be YYYY-MM-DD.`); };
  const number = (v, field, min = 0, max = 100000) => { if (typeof v !== "number" || v < min || v > max) errors.push(`${path}.payload.${field} must be ${min}-${max}.`); };
  switch (op?.type) {
    case "schedule.set":
      rejectUnknown(p, ["schedule"], `${path}.payload`, errors);
      if (!Array.isArray(p.schedule)) errors.push(`${path}.payload.schedule must be an array.`);
      else p.schedule.forEach((s, j) => {
        rejectUnknown(s, ["id", "weekday", "templateId", "time"], `${path}.payload.schedule[${j}]`, errors);
        if (!template(s.templateId)) errors.push(`${path}.payload.schedule[${j}].templateId does not exist.`);
        if (!Number.isInteger(s.weekday) || s.weekday < 0 || s.weekday > 6) errors.push(`${path}.payload.schedule[${j}].weekday must be 0-6.`);
      });
      break;
    case "workout_template.patch":
      rejectUnknown(p, ["templateId", "changes"], `${path}.payload`, errors);
      if (!template(p.templateId)) errors.push(`${path}.payload.templateId does not exist.`);
      if (!p.changes || typeof p.changes !== "object") errors.push(`${path}.payload.changes is required.`);
      else rejectUnknown(p.changes, ["name", "notes", "phaseId", "archived"], `${path}.payload.changes`, errors);
      break;
    case "exercise_prescription.patch":
      rejectUnknown(p, ["templateId", "exerciseId", "changes"], `${path}.payload`, errors);
      if (!template(p.templateId)) errors.push(`${path}.payload.templateId does not exist.`);
      if (!exercise(p.templateId, p.exerciseId)) errors.push(`${path}.payload.exerciseId does not exist in the template.`);
      if (!p.changes || typeof p.changes !== "object") errors.push(`${path}.payload.changes is required.`);
      else rejectUnknown(p.changes, ["name", "sets", "reps", "targetLoad", "loadUnit", "durationMinutes", "restSeconds", "rpe", "rir", "alternatives", "instructions", "coachingNotes", "progressionRule"], `${path}.payload.changes`, errors);
      if (p.changes?.sets != null) number(p.changes.sets, "changes.sets", 1, 20);
      if (p.changes?.rpe != null) number(p.changes.rpe, "changes.rpe", 1, 10);
      if (p.changes?.rir != null) number(p.changes.rir, "changes.rir", 0, 10);
      if (p.changes?.durationMinutes != null) number(p.changes.durationMinutes, "changes.durationMinutes", 1, 600);
      if (p.changes?.restSeconds != null) number(p.changes.restSeconds, "changes.restSeconds", 0, 1800);
      break;
    case "exercise.add":
      rejectUnknown(p, ["templateId", "exercise", "index"], `${path}.payload`, errors);
      if (!template(p.templateId)) errors.push(`${path}.payload.templateId does not exist.`);
      if (!isId(p.exercise?.id) || !p.exercise?.name) errors.push(`${path}.payload.exercise requires id and name.`);
      break;
    case "exercise.replace":
      rejectUnknown(p, ["templateId", "exerciseId", "replacement"], `${path}.payload`, errors);
      if (!exercise(p.templateId, p.exerciseId)) errors.push(`${path}.payload exercise target does not exist.`);
      if (!isId(p.replacement?.id) || !p.replacement?.name) errors.push(`${path}.payload.replacement requires id and name.`);
      break;
    case "nutrition_target.add":
      rejectUnknown(p, ["id", "effectiveDate", "calories", "protein", "fat", "carbs", "note"], `${path}.payload`, errors);
      date(p.effectiveDate, "effectiveDate");
      number(p.calories, "calories", 800, 10000);
      number(p.protein, "protein", 0, 600);
      number(p.fat, "fat", 0, 400);
      number(p.carbs, "carbs", 0, 1500);
      break;
    case "goal.patch":
    case "metric_target.patch":
      rejectUnknown(p, ["goalId", "changes"], `${path}.payload`, errors);
      if (!state?.goals.some(g => g.id === p.goalId)) errors.push(`${path}.payload.goalId does not exist.`);
      if (!p.changes || typeof p.changes !== "object") errors.push(`${path}.payload.changes is required.`);
      else rejectUnknown(p.changes, ["name", "targetValue", "unit", "targetDate", "status", "notes"], `${path}.payload.changes`, errors);
      break;
    case "training_phase.add":
      rejectUnknown(p, ["phase", "activate"], `${path}.payload`, errors);
      if (!isId(p.phase?.id) || !p.phase?.name) errors.push(`${path}.payload.phase requires id and name.`);
      if (p.phase?.startDate) date(p.phase.startDate, "phase.startDate");
      break;
    case "coaching_note.add":
      rejectUnknown(p, ["id", "date", "note"], `${path}.payload`, errors);
      if (typeof p.note !== "string" || !p.note.trim()) errors.push(`${path}.payload.note is required.`);
      if (p.date) date(p.date, "date");
      break;
  }
}

function rejectUnknown(object, allowed, path, errors) {
  if (!object || typeof object !== "object" || Array.isArray(object)) return;
  Object.keys(object).filter(key => !allowed.includes(key)).forEach(key => errors.push(`${path}.${key} is not supported.`));
}

export function previewCoachUpdate(pkg, state) {
  return pkg.operations.map(op => ({ ...op, current: currentForOperation(op, state), proposed: proposedForOperation(op) }));
}

function currentForOperation(op, state) {
  const p = op.payload;
  const program = activeProgram(state);
  const template = program?.workoutTemplates.find(t => t.id === p.templateId);
  const exercise = template?.exercises.find(e => e.id === p.exerciseId);
  switch (op.type) {
    case "schedule.set": return program?.schedule;
    case "workout_template.patch": return pick(template, Object.keys(p.changes || {}));
    case "exercise_prescription.patch": return pick(exercise, Object.keys(p.changes || {}));
    case "exercise.add": return "Not present";
    case "exercise.replace": return exercise;
    case "nutrition_target.add": return nutritionTargetForDate(state, p.effectiveDate);
    case "goal.patch":
    case "metric_target.patch": return pick(state.goals.find(g => g.id === p.goalId), Object.keys(p.changes || {}));
    case "training_phase.add": return "Not present";
    case "coaching_note.add": return "Not present";
    default: return null;
  }
}

function proposedForOperation(op) {
  const p = op.payload;
  if (op.type === "coaching_note.add") return p.note;
  return p.changes || p.schedule || p.exercise || p.replacement || p.phase || p;
}

function pick(object, keys) {
  return Object.fromEntries(keys.map(k => [k, object?.[k]]));
}

export function applyCoachUpdate(state, pkg, acceptedOperationIds, effectiveDate, allowStale = false) {
  const check = validateCoachUpdate(pkg, state);
  if (!check.valid) throw new Error(check.errors.join("\n"));
  if (check.warnings.length && !allowStale) throw new Error("This update is stale. Re-export context and request a fresh update, or explicitly resolve the conflict.");
  if (!validDate(effectiveDate)) throw new Error("Choose a valid effective date.");
  const accepted = pkg.operations.filter(op => acceptedOperationIds.includes(op.id));
  if (!accepted.length) throw new Error("Approve at least one change.");
  const before = clone(state);
  let next = clone(state);
  const programOps = accepted.filter(op => ["schedule.set", "workout_template.patch", "exercise_prescription.patch", "exercise.add", "exercise.replace", "training_phase.add"].includes(op.type));
  if (programOps.length) {
    next = versionProgram(next, program => programOps.forEach(op => applyProgramOperation(program, op)), `Coach update ${pkg.updateId}`, effectiveDate);
    next.programs.find(p => p.id === next.activeProgramId && p.version === next.activeProgramVersion).modificationHistory.push({ at: isoNow(), source: "coach_update", updateId: pkg.updateId, reason: pkg.summary || "Coach update" });
  }
  accepted.filter(op => !programOps.includes(op)).forEach(op => applyStateOperation(next, op, effectiveDate));
  next.coachUpdateHistory.push({
    id: makeId("history"), updateId: pkg.updateId, schemaVersion: pkg.schemaVersion, appliedAt: isoNow(),
    effectiveDate, source: clone(pkg.source), summary: pkg.summary || "", acceptedOperationIds: accepted.map(op => op.id),
    operations: clone(accepted), beforeSnapshot: before, appliedRevision: next.revision + 1, undoneAt: null
  });
  return touch(next);
}

function applyProgramOperation(program, op) {
  const p = op.payload;
  const template = () => program.workoutTemplates.find(t => t.id === p.templateId);
  switch (op.type) {
    case "schedule.set": program.schedule = clone(p.schedule); break;
    case "workout_template.patch": Object.assign(template(), clone(p.changes)); break;
    case "exercise_prescription.patch": Object.assign(template().exercises.find(e => e.id === p.exerciseId), clone(p.changes)); break;
    case "exercise.add": template().exercises.splice(p.index ?? template().exercises.length, 0, clone(p.exercise)); break;
    case "exercise.replace": template().exercises.splice(template().exercises.findIndex(e => e.id === p.exerciseId), 1, clone(p.replacement)); break;
    case "training_phase.add":
      if (p.activate) program.phases.forEach(ph => ph.active = false);
      program.phases.push({ ...clone(p.phase), active: Boolean(p.activate) });
      if (p.activate) program.activePhaseId = p.phase.id;
      break;
  }
}

function applyStateOperation(state, op, effectiveDate) {
  const p = op.payload;
  switch (op.type) {
    case "nutrition_target.add": state.nutritionTargets.push({ id: p.id || makeId("nutrition"), ...clone(p), effectiveDate: p.effectiveDate || effectiveDate }); break;
    case "goal.patch":
    case "metric_target.patch": Object.assign(state.goals.find(g => g.id === p.goalId), clone(p.changes)); break;
    case "coaching_note.add": state.coachingNotes.push({ id: p.id || makeId("coach_note"), date: p.date || effectiveDate, note: p.note, reason: op.reason }); break;
  }
}

export function undoCoachUpdate(state, historyId) {
  const entry = state.coachUpdateHistory.find(h => h.id === historyId);
  if (!entry || entry.undoneAt) throw new Error("This update cannot be undone.");
  const restored = clone(entry.beforeSnapshot);
  restored.coachUpdateHistory = clone(state.coachUpdateHistory).map(h => h.id === historyId ? { ...h, beforeSnapshot: entry.beforeSnapshot, undoneAt: isoNow() } : h);
  restored.modifiedAt = isoNow();
  restored.revision = state.revision + 1;
  return restored;
}

export function exportCoachingContext(state, options = {}) {
  const from = options.from || "1900-01-01";
  const to = options.to || "9999-12-31";
  const within = item => item.date >= from && item.date <= to;
  const selected = new Set(options.include || ["profile", "goals", "training", "nutrition", "weight", "performance", "activity", "adherence", "recovery"]);
  const out = { contextVersion: "1.0", exportedAt: isoNow(), dateRange: { from, to }, appRevision: state.revision };
  if (selected.has("profile")) out.profile = clone(state.profile);
  if (selected.has("goals")) out.activeGoals = state.goals.filter(g => g.status === "active").map(g => clone(g));
  if (selected.has("training")) out.activeProgram = clone(activeProgram(state));
  if (selected.has("nutrition")) out.nutritionTargets = state.nutritionTargets.filter(t => t.effectiveDate <= to).map(clone);
  if (selected.has("weight")) out.bodyWeight = state.measurements.filter(m => m.metricId === "metric_body_weight" && within(m)).map(clone);
  if (selected.has("performance")) out.completedWorkouts = state.completedWorkouts.filter(within).map(w => ({ date: w.date, templateName: w.templateName, programVersion: w.programVersion, actualExercises: w.actualExercises }));
  if (selected.has("activity")) out.activity = state.activityLogs.filter(within).map(clone);
  if (selected.has("adherence")) out.adherence = adherenceSummary(state, from, to);
  if (selected.has("recovery")) out.recovery = state.recoveryNotes.filter(within).map(clone);
  return out;
}

function adherenceSummary(state, from, to) {
  const completed = state.completedWorkouts.filter(w => w.date >= from && w.date <= to).length;
  const weeks = Math.max(1, Math.ceil((new Date(to) - new Date(from)) / 604800000));
  const prescribed = Math.max(1, (activeProgram(state)?.schedule.length || 0) * weeks);
  return { completed, prescribed, percent: Math.round(completed / prescribed * 100) };
}

export function createBackup(state) {
  return { backupFormat: "athlete-os", backupVersion: 1, createdAt: isoNow(), data: clone(state) };
}

export function restoreBackup(backup) {
  if (!backup || backup.backupFormat !== "athlete-os" || !Number.isInteger(backup.backupVersion)) throw new Error("Not a valid Athlete OS backup.");
  return migrateState(backup.data);
}

export function migrateState(input) {
  if (!input || typeof input !== "object") throw new Error("Backup data is missing.");
  const state = clone(input);
  if (!state.schemaVersion) {
    state.schemaVersion = 1;
    state.coachUpdateHistory ||= [];
    state.migrationHistory ||= [];
    state.migrationHistory.push({ from: 0, to: 1, at: isoNow() });
  }
  if (state.schemaVersion > APP_SCHEMA_VERSION) throw new Error(`Backup schema ${state.schemaVersion} is newer than this app supports.`);
  const required = ["programs", "nutritionTargets", "goals", "completedWorkouts", "coachUpdateHistory"];
  required.forEach(k => { if (!Array.isArray(state[k])) throw new Error(`Backup is missing ${k}.`); });
  if (!activeProgram(state)) throw new Error("Backup does not contain its active program version.");
  state.modifiedAt = isoNow();
  return state;
}

function isId(v) { return typeof v === "string" && /^[A-Za-z0-9][A-Za-z0-9_-]{2,79}$/.test(v); }
function validDate(v) { return typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) && !Number.isNaN(Date.parse(`${v}T00:00:00Z`)); }
function validDateTime(v) { return typeof v === "string" && !Number.isNaN(Date.parse(v)); }
